// MoSCoWVisualization - Grouped lists with color coding for MoSCoW framework
// Implements US-5.3: MoSCoW Grouped Lists with Color Coding

import React, { useState } from 'react';
import styles from './MoSCoWVisualization.module.css';
import { Item } from '../../types/domain';

interface MoSCoWVisualizationProps {
  items: Item[];
}

// MOSCOW category configuration
const MOSCOW_CATEGORIES = [
  {
    key: 'Must Have',
    label: 'Must Have',
    description: 'Kritische Themen / Elemente, die geliefert werden müssen',
    colorClass: styles.categoryMust,
  },
  {
    key: 'Should Have',
    label: 'Should Have',
    description: 'Wichtig, aber nicht kritisch',
    colorClass: styles.categoryShould,
  },
  {
    key: 'Could Have',
    label: 'Could Have',
    description: 'Wünschenswert, aber nicht notwendig',
    colorClass: styles.categoryCould,
  },
  {
    key: "Won't Have",
    label: "Won't Have",
    description: 'Nicht im Rahmen dieser Iteration',
    colorClass: styles.categoryWont,
  },
];

/**
 * RENDER MoSCoW grouped lists visualization
 * - Four collapsible sections for each category
 * - Color-coded sections with item counts
 * - Uncategorized section for unscored items
 * - Empty state messages for empty categories
 */
export const MoSCoWVisualization: React.FC<MoSCoWVisualizationProps> = ({ items }) => {
  // STATE for collapsed sections (all expanded by default)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  /**
   * GROUP items by MoSCoW category
   */
  const groupedItems = React.useMemo(() => {
    const groups: Record<string, Item[]> = {
      'Must Have': [],
      'Should Have': [],
      'Could Have': [],
      "Won't Have": [],
      'Uncategorized': [],
    };

    items.forEach((item) => {
      // EXTRACT category from item scores
      const categoryScore = item.scores?.find((s) => s.criterion === 'category');
      const category = categoryScore ? String(categoryScore.value) : null;

      if (category && groups[category]) {
        groups[category]!.push(item);
      } else {
        groups['Uncategorized']!.push(item);
      }
    });

    return groups;
  }, [items]);

  /**
   * TOGGLE section collapse state
   */
  const toggleSection = (categoryKey: string) => {
    setCollapsedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryKey)) {
        newSet.delete(categoryKey);
      } else {
        newSet.add(categoryKey);
      }
      return newSet;
    });
  };

  // CHECK if workspace has no scored items
  const totalScoredItems = items.filter((item) =>
    item.scores?.some((s) => s.criterion === 'category')
  ).length;

  // RENDER empty state if no items are scored
  if (totalScoredItems === 0) {
    return (
      <div className={styles.emptyState}>
        <p>Füge Themen / Elemente hinzu und weise Kategorien zu, um Ergebnisse zu sehen</p>
      </div>
    );
  }

  return (
    <div className={styles.moscowVisualization}>
      {/* RENDER category sections */}
      {MOSCOW_CATEGORIES.map((category) => {
        const categoryItems = groupedItems[category.key]!;
        const isCollapsed = collapsedSections.has(category.key);
        const isEmpty = categoryItems.length === 0;

        return (
          <div key={category.key} className={`${styles.categorySection} ${category.colorClass}`}>
            {/* SECTION header with count */}
            <button
              className={styles.sectionHeader}
              onClick={() => toggleSection(category.key)}
              aria-expanded={!isCollapsed}
            >
              <div className={styles.headerContent}>
                <h3 className={styles.categoryTitle}>
                  {category.label}
                  <span className={styles.itemCount}>({categoryItems.length})</span>
                </h3>
                <p className={styles.categoryDescription}>{category.description}</p>
              </div>
              <span className={styles.collapseIcon}>{isCollapsed ? '▶' : '▼'}</span>
            </button>

            {/* SECTION content */}
            {!isCollapsed && (
              <div className={styles.sectionContent}>
                {isEmpty ? (
                  <p className={styles.emptyMessage}>Keine Themen / Elemente in dieser Kategorie</p>
                ) : (
                  <ul className={styles.itemList}>
                    {categoryItems.map((item) => (
                      <li key={item.id} className={styles.itemCard}>
                        <span className={styles.itemTitle}>{item.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* UNCATEGORIZED section (only show if there are uncategorized items) */}
      {groupedItems['Uncategorized']!.length > 0 && (
        <div className={`${styles.categorySection} ${styles.uncategorized}`}>
          <button
            className={styles.sectionHeader}
            onClick={() => toggleSection('Uncategorized')}
            aria-expanded={!collapsedSections.has('Uncategorized')}
          >
            <div className={styles.headerContent}>
              <h3 className={styles.categoryTitle}>
                Nicht kategorisiert
                <span className={styles.itemCount}>({groupedItems['Uncategorized']!.length})</span>
              </h3>
              <p className={styles.categoryDescription}>Themen / Elemente ohne zugewiesene Kategorie</p>
            </div>
            <span className={styles.collapseIcon}>
              {collapsedSections.has('Uncategorized') ? '▶' : '▼'}
            </span>
          </button>

          {!collapsedSections.has('Uncategorized') && (
            <div className={styles.sectionContent}>
              <ul className={styles.itemList}>
                {groupedItems['Uncategorized']!.map((item) => (
                  <li key={item.id} className={styles.itemCard}>
                    <span className={styles.itemTitle}>{item.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
